<template>
  <div>
    <LcCard body-class="py-100">
      <PayoutMethodAeropayButton :loading="isLoading" @click="handleAeropay" />

      <Teleport to="body">
        <div id="widget" />
      </Teleport>
    </LcCard>

    <section
      class="paragraph-small-text text-secondary-secondary flex flex-col items-start gap-100 mt-100"
    >
      <span>
        {{ $t('paymentmethod.aeropay.information.description') }}
      </span>

      <img
        :alt="$t('paymentmethod.aeropay.information.image.alt')"
        :height="32"
        src="@/assets/img/payment/aeropay-grid.svg"
        :width="142"
      />
    </section>

    <div
      v-if="!storedAccounts"
      class="manual-transfer inline-flex items-center px-200 bg-secondary-secondary mt-300 rounded-m"
    >
      <p class="manual-transfer-text paragraph-regular">
        {{ $t('account.payout.aeropay.manualtransfer.text') }}
      </p>
      <button
        class="manual-transfer-button text-link font-medium ml-050"
        type="button"
        @click="emit('manual-transfer-requested')"
      >
        {{ $t('account.payout.aeropay.manualtransfer.lint.text') }}
      </button>
    </div>

    <LazyPayoutMethodAeropayMfaModal
      v-if="showMFAModal && aeropayExistingUser"
      v-bind="{ phoneNumber }"
      :user="aeropayExistingUser"
      @close="showMFAModal = false"
    />
  </div>
</template>

<script lang="ts" setup>
import type { WidgetEventSuccessType } from 'aerosync-web-sdk'

import {
  type AeropayExistingUser,
  type AeropayNewUser,
  type AeropayReturningUser,
  PayOutAccountType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { PayoutStoredAccounts } from './PayoutStoredComponentMapper'

type AeropayUser =
  | Omit<AeropayExistingUser, 'email'>
  | Omit<AeropayNewUser, 'consumerId' | 'email'>
  | Omit<AeropayReturningUser, 'email'>

type PayoutNewMethodAeropayEmits = {
  (e: 'manual-transfer-requested'): void
}

type PayoutNewMethodAeropayProps = {
  storedAccounts?: PayoutStoredAccounts
}

defineProps<PayoutNewMethodAeropayProps>()
const emit = defineEmits<PayoutNewMethodAeropayEmits>()

const isAeroPayNewUser = (user: AeropayUser): user is AeropayNewUser =>
  (user as AeropayNewUser).aggregatorToken !== undefined

const isAeroPayReturningUser = (user: AeropayUser): user is AeropayReturningUser =>
  (user as AeropayReturningUser).__typename === 'AeropayReturningUser'

const isAeropayExistingUser = (user: AeropayUser): user is AeropayExistingUser =>
  (user as AeropayExistingUser).displayMessage !== undefined

const licensedTerritory = useLicensedTerritory({ raw: true })

const { init, onError: onAeroPayError, onSuccess: onAeropaySuccess, open } = useAeroPay('widget')
const { addPayOutInstrument, publish } = usePayoutSingleton()
const { t } = useI18n()
const { addToast, addToastError } = useToaster()
const { log } = useDatadog()
const apolloClient = useNuxtApp().$apollo.defaultClient

const { result } = useUserDataQuery({
  fetchPolicy: 'cache-only',
})

const { loading: registerUserLoading, mutate: registerUser } = useRegisterAeropayUserMutation()

const showMFAModal = ref(false)
const aeroPayInstrumentLoading = ref(false)
const aeropayExistingUser = ref<AeropayExistingUser>()
const aeropayNewUser = ref<AeropayNewUser>()

const phoneNumber = computed(
  () => `${result.value?.player.phone.countryCode} ${result.value?.player.phone.number}`,
)

const isLoading = computed(() => registerUserLoading.value || aeroPayInstrumentLoading.value)

const handleAeropay = async () => {
  const player = result.value?.player
  if (!player) return

  try {
    const response = await registerUser({
      input: {
        email: player.email,
        firstName: player.givenName,
        lastName: player.familyName,
        licensedTerritory,
        phoneNumber: phoneNumber.value,
      },
    })

    const aeropayUser = response?.data?.registerUser
    if (!aeropayUser) return

    if (isGraphqlFailure(aeropayUser)) {
      if (
        hasGraphqlError(aeropayUser.errors, 'Payment_AeropayRegisterUserError_InvalidPhoneNumber')
      ) {
        return addToast(
          {
            autoClose: false,
            message: {
              translationKey:
                'account.payout.aeroppay.toast.error.validation.aeropayuser.invalidphonenumber.message',
              translationVariables: {
                cta: {
                  text: t(
                    'account.payout.aeroppay.toast.error.validation.aeropayuser.invalidphonenumber.cta',
                  ),
                  to: '/account/personal-data/edit-phone-number',
                  type: 'link',
                },
              },
            },
            tracking: {
              action: 'payout',
              topicKey: 'register_aeropay_user_failed',
            },
            type: 'danger',
          },
          'aeropay-invalid-phonenumber',
        )
      }

      return addToast({
        headline: t('common.toast.error.general.headline'),
        message: t('common.toast.error.general.message'),
        tracking: {
          action: 'payout',
          topicKey: 'register_aeropay_user_failed',
        },
        type: 'danger',
      })
    }

    if (isAeroPayReturningUser(aeropayUser)) {
      await apolloClient.refetchQueries({
        include: [PayOutAccountsDocument],
      })
      publish(aeropayUser?.preferredPayOutAccountId ?? undefined)
      return
    }

    if (isAeroPayNewUser(aeropayUser)) {
      aeropayNewUser.value = aeropayUser
      init(aeropayUser.aggregatorToken)
      open()
      return
    }

    if (isAeropayExistingUser(aeropayUser)) {
      aeropayExistingUser.value = aeropayUser
      showMFAModal.value = true
      return
    }
  } catch (error) {
    log('error', {
      component: 'PayoutNewMethodAeropay.vue',
      message: 'Failed to register aeropay user',
      messageContext: { error },
    })

    addToastError({
      error,
      prefix: 'account.payout.aeroppay.toast.error',
      tracking: {
        action: 'payout',
        topicKey: 'register_aeropay_user_failed',
      },
    })
  }
}

onAeroPayError((event: string) => {
  log('error', {
    component: 'PayoutNewMethodAeropay.vue',
    message: 'Failed to finish aeropay widget',
    messageContext: { event },
  })
})

onAeropaySuccess(async (event: WidgetEventSuccessType) => {
  if (!aeropayNewUser.value) return

  try {
    aeroPayInstrumentLoading.value = true

    await addPayOutInstrument(PayOutAccountType.Aeropay, [
      { key: 'aeropayUserId', value: aeropayNewUser.value.userId },
      { key: 'aerosyncUserId', value: event.user_id },
      { key: 'aerosyncUserPassword', value: event.user_password },
    ])
  } catch (error) {
    log('error', {
      component: 'PayoutNewMethodAeropay.vue',
      message: 'Failed to save payout instrument',
      messageContext: { error },
    })

    addToastError(
      {
        error,
        prefix: 'account.payout.toast.error',
        tracking: {
          action: 'payout',
          topicKey: 'add_payout_instrument_failed',
        },
      },
      {
        fallback: {
          message: {
            translationKey: 'account.payout.toast.error.addpayoutinstrument.fallback.message',
            translationVariables: {
              contactSupport: {
                text: t('common.sentences.contactsupport').toLowerCase(),
                to: '/service?chat=true',
                type: 'link',
              },
            },
          },
          type: 'danger',
        },
      },
    )
  } finally {
    aeroPayInstrumentLoading.value = false
  }
})
</script>

<style scoped lang="scss">
.manual-transfer {
  height: 3rem;

  &-button {
    cursor: pointer;
  }
}
</style>
